import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Modal, Typography } from '@orijinworks/frontend-commons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DeleteModalStyles } from '../../Feeds.style';
import { useAttachFormModalHook } from './useAttachFormModalHook';

const formIdEquals = (a, b) => String(a) === String(b);

const ATTACH_FORM_LIST_ITEM_ICON_SX = {
  minWidth: 42,
  alignSelf: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const FormListItem = React.memo(({ form, selected, onToggle }) => (
  <ListItemButton
    alignItems="center"
    dense
    selected={selected}
    onClick={() => onToggle(form.id)}
    data-form-id={form.id}
  >
    <ListItemIcon sx={ATTACH_FORM_LIST_ITEM_ICON_SX}>
      <Checkbox
        edge="start"
        checked={selected}
        tabIndex={-1}
        disableRipple
        size="small"
        sx={{ p: 0.5 }}
        inputProps={{ 'aria-labelledby': `attach-form-label-${form.id}` }}
      />
    </ListItemIcon>
    <ListItemText
      id={`attach-form-label-${form.id}`}
      primary={form.name || 'Untitled form'}
      primaryTypographyProps={{ sx: { lineHeight: 1.43 } }}
    />
  </ListItemButton>
));

FormListItem.displayName = 'FormListItem';

const AttachFormModalComponent = ({ open, onClose, onFormConfirm, attachedForms = [] }) => {
  const { forms, loading, error, handleClose } = useAttachFormModalHook(open, onClose);
  const [selectedIds, setSelectedIds] = useState([]);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelectedIds((attachedForms || []).map((f) => f.id).filter((id) => id != null));
    }
    prevOpenRef.current = open;
  }, [open, attachedForms]);

  const toggleFormId = useCallback((formId) => {
    setSelectedIds((prev) => {
      const has = prev.some((id) => formIdEquals(id, formId));
      if (has) {
        return prev.filter((id) => !formIdEquals(id, formId));
      }
      return [...prev, formId];
    });
  }, []);

  const handleContinue = useCallback(async () => {
    if (typeof onFormConfirm !== 'function') {
      return;
    }
    const selectedForms = forms.filter((f) =>
      selectedIds.some((sid) => formIdEquals(sid, f.id)),
    );
    onFormConfirm(selectedForms);
  }, [onFormConfirm, selectedIds, forms]);

  const selectDisabled = loading || Boolean(error) || forms.length === 0;

  const listContent = useMemo(() => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={32} aria-label="Loading forms" />
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    if (forms.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No forms available.
        </Typography>
      );
    }
    return (
      <Box
        sx={{
          maxHeight: 'min(480px, calc(100vh - 280px))',
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        <List dense disablePadding role="listbox" aria-multiselectable="true">
          {forms.map((form) => (
            <FormListItem
              key={String(form.id)}
              form={form}
              selected={selectedIds.some((sid) => formIdEquals(sid, form.id))}
              onToggle={toggleFormId}
            />
          ))}
        </List>
      </Box>
    );
  }, [loading, error, forms, selectedIds, toggleFormId]);

  return (
    <Modal
      key={open ? 'attach-form-open' : 'attach-form-closed'}
      isSnackBarShown={false}
      cancelBtnText="Close"
      actionBtnText="Apply"
      heading="Attach forms"
      labelledBy="attach-form-modal-title"
      describedBy="attach-form-modal-description"
      open={open}
      onClose={handleClose}
      onContinue={handleContinue}
      continueButtonProps={{ disabled: selectDisabled }}
      modalType={{ type: 'confirmation', status: 'info', size: 'medium' }}
      showCrossIcon
      styles={{
        mainWrapperStyles: { height: 'auto' },
      }}
      mainModalStyles={DeleteModalStyles}
    >
      <Typography variant="body2" id="attach-form-modal-description" sx={{ mb: 2 }}>
        Select one or more forms to attach.
      </Typography>
      {listContent}
    </Modal>
  );
};

const AttachFormModal = React.memo(AttachFormModalComponent);

export default AttachFormModal;
